import { useEffect, useState } from 'react'
import { useTranslation } from 'react-i18next'

import { EID } from '@/lib/element-ids.registry'
import { useStartListingAudit, useStartBatchListingAudit } from '@/hooks/useListingBuilder'

import { MarketTldSelector } from '../MarketTldSelector'
import type { RegisterSubmit, SourceSubmitStatus } from './sourceSubmit'

const INPUT_CLS =
  'w-full px-3 py-2 bg-neutral-50 dark:bg-neutral-900 border border-neutral-200 dark:border-neutral-700 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-orange-500/20 focus:border-orange-500'
const ASIN_RE = /^[A-Z0-9]{10}$/

const parseAsins = (raw: string): string[] =>
  raw
    .split('\n')
    .map((line) => line.trim().toUpperCase())
    .filter(Boolean)

export function ListingAsinTab({
  onStarted,
  onBatchStarted,
  mode,
  registerSubmit,
  onStatusChange,
}: {
  onStarted: (jobId: string) => void
  onBatchStarted?: () => void
  mode?: 'small' | 'standard' | 'custom'
  /** Lifts the submit handler to the dialog footer (button lives at the modal bottom). */
  registerSubmit?: RegisterSubmit
  /** Reports submit-readiness so the footer button can reflect it. */
  onStatusChange?: (status: SourceSubmitStatus) => void
}) {
  const { t } = useTranslation()
  const [batch, setBatch] = useState(false)
  const [asin, setAsin] = useState('')
  const [asinsText, setAsinsText] = useState('')
  const [marketTld, setMarketTld] = useState('de')
  const single = useStartListingAudit()
  const multi = useStartBatchListingAudit()

  const asins = parseAsins(asinsText)
  const isValid = batch
    ? asins.length >= 1 && asins.length <= 20 && asins.every((a) => ASIN_RE.test(a))
    : ASIN_RE.test(asin.trim().toUpperCase())
  const isPending = single.isPending || multi.isPending
  const error = single.error || multi.error
  const canSubmit = isValid && !isPending

  const handleSubmit = () => {
    if (!canSubmit) return
    if (batch) {
      multi.mutate(
        { asins, market_tld: marketTld, mode },
        { onSuccess: () => onBatchStarted?.() },
      )
    } else {
      single.mutate(
        { source_ref: asin.trim().toUpperCase(), source_type: 'asin', market_tld: marketTld, mode },
        { onSuccess: (r) => onStarted(r.job_id) },
      )
    }
  }

  // Keep the parent's footer button wired to the latest submit closure (ref
  // assignment — no re-render, no loop).
  useEffect(() => {
    registerSubmit?.(handleSubmit)
  })
  // Report readiness only when a primitive actually changes (loop-free).
  useEffect(() => {
    onStatusChange?.({
      canSubmit,
      isPending,
      submitId: batch
        ? EID['listing-builder-asin-batch-submit-btn']
        : EID['listing-builder-asin-submit-btn'],
      labelKey: batch ? 'listingBuilder.source.asin.batchSubmit' : 'listingBuilder.source.start',
    })
  }, [canSubmit, isPending, batch, onStatusChange])

  return (
    <div className="space-y-5">
      <div className="flex items-center justify-between">
        <label className="block text-sm font-medium text-neutral-700 dark:text-neutral-300">
          {t('listingBuilder.source.asin.label')}
        </label>
        <button id={EID['listing-builder-asin-batch-toggle']} type="button"
          onClick={() => setBatch((b) => !b)} aria-pressed={batch}
          className="text-xs font-medium text-orange-500 hover:text-orange-600">
          {t('listingBuilder.source.asin.batchToggle')}
        </button>
      </div>

      {batch ? (
        <div>
          <textarea id={EID['listing-builder-asin-batch-input']} value={asinsText} rows={5}
            onChange={(e) => setAsinsText(e.target.value)} placeholder={'B0XXXXXXXX'}
            className={INPUT_CLS} aria-invalid={asins.length > 0 && !isValid} />
          <p className="mt-1.5 text-xs text-neutral-500 dark:text-neutral-400">
            {t('listingBuilder.source.asin.batchHint')}
          </p>
        </div>
      ) : (
        <input id={EID['listing-builder-asin-input']} value={asin}
          onChange={(e) => setAsin(e.target.value)} placeholder="B0XXXXXXXX"
          className={INPUT_CLS} aria-invalid={asin.length > 0 && !isValid} />
      )}

      <div>
        <label className="block text-sm font-medium text-neutral-700 dark:text-neutral-300 mb-1.5">
          {t('listingBuilder.source.marketplace')}
        </label>
        <MarketTldSelector value={marketTld} onChange={setMarketTld} />
      </div>

      {error && (
        <p className="text-sm text-red-600 dark:text-red-400">{t('listingBuilder.source.error')}</p>
      )}
    </div>
  )
}
